// ════════════════════════════════════════════════════════════════════
// LYRAEA — entry point
// ────────────────────────────────────────────────────────────────────
// Its own app, on the shared Empire backend. Identity model is the IE
// contract, unchanged:
//   1. ?uid=UID in the URL means another title handed this listener off.
//      Adopt it, strip the param (history.replaceState), skip our login.
//   2. Otherwise: email + password. Firebase Auth gives a stable uid, so
//      a library follows a listener across devices.
//   3. Ensure profiles/{uid} exists so nothing downstream sees null.
//   4. Never signOut() on leaving — only an explicit OUT does that.
//      A listener who walks back to the Pier must still be signed in.
//
// ── THE HANDOFF IS A HINT, NOT AN IDENTITY ───────────────────────────
// A uid in a URL is client-supplied and therefore forgeable. It selects
// which profile to *display*; it never authorises a write. Every writer
// in this codebase checks auth.currentUser.uid, and the rules seal on
// profiles/$uid is the real enforcement. This is why arriving with
// someone else's uid in the URL gets you a read-only look at a library
// and nothing more.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import { ref, get, set } from "firebase/database";
import {
  onAuthStateChanged, createUserWithEmailAndPassword,
  signInWithEmailAndPassword, signOut, browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import { db, auth } from "./firebase";
import Lyraea from "./Lyraea";
import "./index.css";

const C = {
  void: "var(--void)", slab: "var(--slab)", edge: "var(--edge)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", bone: "var(--bone)",
  ash: "var(--ash)", smoke: "var(--smoke)", blood: "var(--blood)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

function consumeUidParam() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("uid");
    if (!uid) return null;
    params.delete("uid");
    const qs = params.toString();
    window.history.replaceState({}, "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    return uid;
  } catch { return null; }
}

async function ensureProfile(uid, email) {
  const snap = await get(ref(db, `profiles/${uid}`));
  if (snap.exists()) return { ...snap.val(), uid };
  // A minimal profile only. Lyraea does NOT invent identity fields that
  // belong to Pierre (gender, companion, faction) — it writes the bare
  // minimum so downstream never sees null, and lets the Pier own the rest.
  const seed = { uid, email: email || "", createdAt: Date.now(), displayName: "" };
  await set(ref(db, `profiles/${uid}`), seed);
  return seed;
}

export default function App() {
  const [uid, setUid] = useState(null);
  const [profile, setProfile] = useState(null);
  const [booting, setBooting] = useState(true);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [mode, setMode] = useState("in");   // "in" | "up"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Persist the session across reloads and across a walk to another
    // title and back. Required by the IE handoff contract.
    setPersistence(auth, browserLocalPersistence).catch(() => {});
    const handoff = consumeUidParam();
    const unsub = onAuthStateChanged(auth, async (user) => {
      const id = user?.uid || handoff || null;
      if (!id) { setUid(null); setProfile(null); setBooting(false); return; }
      try {
        const p = await ensureProfile(id, user?.email);
        setUid(id); setProfile(p);
      } catch (e) {
        setErr(e.message || "Could not open your profile.");
      }
      setBooting(false);
    });
    return unsub;
  }, []);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      if (mode === "up") await createUserWithEmailAndPassword(auth, email.trim(), pass);
      else await signInWithEmailAndPassword(auth, email.trim(), pass);
    } catch (e) {
      setErr((e.code || e.message || "").replace("auth/", "").replace(/-/g, " "));
    } finally { setBusy(false); }
  };

  if (booting) {
    return (
      <Center>
        <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: ".34em", color: C.smoke, animation: "lyPulse 1.4s infinite" }}>
          TUNING
        </div>
      </Center>
    );
  }

  if (!uid) {
    return (
      <Center>
        <div style={{ width: "min(360px, 90vw)", animation: "lyRise .35s both" }}>
          <div style={{ fontFamily: C.mono, fontSize: 8.5, letterSpacing: ".3em", color: C.smoke }}>
            THE ILLEGAL EMPIRE
          </div>
          <div style={{
            fontFamily: C.display, fontWeight: 800, fontSize: 44, lineHeight: 1, margin: "4px 0 6px",
            background: `linear-gradient(105deg, #6b5220 0%, ${C.gold} 20%, ${C.goldHi} 32%, #fffbe8 40%, ${C.goldHi} 52%, ${C.gold} 70%, #8a6a26 100%)`,
            WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>LYRAEA</div>
          <div style={{ fontFamily: C.display, fontStyle: "italic", fontSize: 15, color: C.ash, marginBottom: 22 }}>
            The listening room.
          </div>

          <input style={input} placeholder="email" value={email} autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} />
          <div style={{ height: 8 }} />
          <input style={input} placeholder="password" type="password" value={pass}
            autoComplete={mode === "up" ? "new-password" : "current-password"}
            onChange={(e) => setPass(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()} />

          {err && (
            <div style={{ fontFamily: C.mono, fontSize: 10, color: C.blood, marginTop: 10, letterSpacing: ".08em" }}>
              {err.toUpperCase()}
            </div>
          )}

          <button onClick={busy ? undefined : submit} style={{
            width: "100%", marginTop: 14, padding: "13px 0", border: "none",
            background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`, color: C.void,
            fontFamily: C.mono, fontSize: 10, letterSpacing: ".26em",
          }}>{busy ? "…" : mode === "up" ? "CREATE MARK" : "ENTER"}</button>

          <div onClick={() => { setMode(mode === "up" ? "in" : "up"); setErr(""); }} style={{
            marginTop: 12, fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em",
            color: C.smoke, cursor: "pointer", textAlign: "center",
          }}>
            {mode === "up" ? "I ALREADY HAVE A MARK" : "NEW HERE — CREATE A MARK"}
          </div>

          <div style={{ fontFamily: C.sans, fontSize: 12.5, color: C.smoke, marginTop: 20, lineHeight: 1.5, textAlign: "center" }}>
            One login for the whole Empire. The Mark you make here is the same
            one that walks the Pier.
          </div>
        </div>
      </Center>
    );
  }

  return (
    <Lyraea
      uid={uid}
      profile={profile}
      onLogout={() => signOut(auth).then(() => { setUid(null); setProfile(null); })}
    />
  );
}

const input = {
  width: "100%", boxSizing: "border-box", background: "var(--void)",
  border: "1px solid var(--edge)", color: "var(--bone)",
  fontFamily: "var(--sans)", fontSize: 16, padding: "11px 12px", outline: "none",
};

function Center({ children }) {
  return (
    <div style={{
      minHeight: "100%", display: "grid", placeItems: "center",
      padding: 22, background: "var(--void)",
    }}>{children}</div>
  );
}
